import type { SuggestionLanguagesContextDto } from "@shelflist/shared";
import { AUTOMATIC_LANGUAGE_MIN_BOOKS, NOT_A_LANGUAGE } from "@shelflist/shared";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useErrorText } from "@/lib/errors";
import { ISO_639_1_CODES } from "@/lib/iso-639-1";

export interface SuggestionLanguagesValue {
  /** `null`: automatic (the languages of the library). */
  languages: string[] | null;
  includeUnknown: boolean;
}

/**
 * "Languages for suggestions" of the admin settings: automatic (the library's languages) or an
 * explicit list with counts, a way to add another language, and the switch for books without
 * language information. Controlled; the settings form saves it.
 */
export function SuggestionLanguagesField({
  value,
  context,
  error,
  onChange,
}: {
  value: SuggestionLanguagesValue;
  context: SuggestionLanguagesContextDto | undefined;
  /** Error code of the server's field error for `authorSuggestions.languages`. */
  error?: string;
  onChange: (value: SuggestionLanguagesValue) => void;
}) {
  const { t, i18n } = useTranslation("instanceSettings");
  const { t: tc } = useTranslation("common");
  const errorText = useErrorText();
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames(i18n.language, { type: "language" });
    } catch {
      return undefined;
    }
  }, [i18n.language]);
  const name = useCallback(
    (code: string): string => {
      try {
        return names?.of(code) ?? code;
      } catch {
        return code;
      }
    },
    [names],
  );
  const automatic = value.languages === null;
  const [added, setAdded] = useState<string[]>([]);
  const [toAdd, setToAdd] = useState("");

  // Every language the admin can pick: the library's, the cached editions', the chosen ones and
  // the ones added by hand, with their counts, ordered by count.
  const options = useMemo(() => {
    const rows = new Map<string, { code: string; books: number; editions: number }>();
    const row = (code: string) => {
      let entry = rows.get(code);
      if (!entry) {
        entry = { code, books: 0, editions: 0 };
        rows.set(code, entry);
      }
      return entry;
    };
    for (const l of context?.resolved ?? []) row(l.code).books = l.books;
    for (const l of context?.belowThreshold ?? []) row(l.code).books = l.books;
    for (const l of context?.cached ?? [])
      if (!NOT_A_LANGUAGE.has(l.code)) row(l.code).editions = l.editions;
    for (const code of value.languages ?? []) row(code);
    for (const code of added) row(code);
    return [...rows.values()].sort(
      (a, b) =>
        b.books - a.books ||
        b.editions - a.editions ||
        name(a.code).localeCompare(name(b.code), i18n.language),
    );
  }, [context, value.languages, added, name, i18n.language]);

  const resolved = context?.resolved ?? [];
  const belowThreshold = context?.belowThreshold ?? [];
  const addable = ISO_639_1_CODES.filter((code) => !options.some((o) => o.code === code))
    .map((code) => ({ code, label: name(code) }))
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  const chosen = value.languages ?? [];

  const counts = (books: number, editions: number) =>
    [
      books > 0 ? t("languages.books", { count: books }) : undefined,
      editions > 0 ? t("languages.editions", { count: editions }) : undefined,
    ]
      .filter(Boolean)
      .join(", ");

  return (
    <fieldset className="grid gap-3 border-t pt-4" data-testid="suggestion-languages">
      <legend className="text-base font-semibold">{tc("suggestionLanguages.title")}</legend>
      <p className="text-muted-foreground text-sm">{t("languages.description")}</p>

      <div className="flex items-start gap-3">
        <input
          id="languages-mode-automatic"
          type="radio"
          name="languages-mode"
          className="accent-primary mt-1 size-4"
          checked={automatic}
          onChange={() => onChange({ ...value, languages: null })}
        />
        <div className="grid gap-1">
          <Label htmlFor="languages-mode-automatic">{t("languages.automatic")}</Label>
          <p className="text-muted-foreground text-sm">
            {t("languages.automaticHelp", { count: AUTOMATIC_LANGUAGE_MIN_BOOKS })}
          </p>
          {automatic ? (
            <div className="text-sm" data-testid="languages-resolved">
              <p>
                {t("languages.resolved", {
                  list: resolved
                    .map((l) =>
                      l.books > 0 ? `${name(l.code)} (${counts(l.books, 0)})` : name(l.code),
                    )
                    .join(", "),
                })}
              </p>
              {belowThreshold.length > 0 ? (
                <p className="text-muted-foreground">
                  {t("languages.belowThreshold", {
                    count: AUTOMATIC_LANGUAGE_MIN_BOOKS,
                    list: belowThreshold
                      .map((l) => `${name(l.code)} (${counts(l.books, 0)})`)
                      .join(", "),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="languages-mode-choose"
          type="radio"
          name="languages-mode"
          className="accent-primary mt-1 size-4"
          checked={!automatic}
          onChange={() =>
            onChange({
              ...value,
              languages: resolved.length > 0 ? resolved.map((l) => l.code) : ["de", "en"],
            })
          }
        />
        <div className="grid gap-1">
          <Label htmlFor="languages-mode-choose">{t("languages.choose")}</Label>
          <p className="text-muted-foreground text-sm">{t("languages.chooseHelp")}</p>
        </div>
      </div>

      {!automatic ? (
        <div className="grid gap-3 pl-7">
          <ul className="grid gap-2" aria-label={t("languages.listLabel")}>
            {options.map((option) => {
              const id = `language-${option.code}`;
              const checked = chosen.includes(option.code);
              return (
                <li key={option.code} className="flex items-start gap-3">
                  <input
                    id={id}
                    type="checkbox"
                    className="accent-primary mt-1 size-4"
                    checked={checked}
                    onChange={(event) =>
                      onChange({
                        ...value,
                        languages: event.target.checked
                          ? [...chosen, option.code]
                          : chosen.filter((code) => code !== option.code),
                      })
                    }
                  />
                  <Label htmlFor={id} className="grid gap-0 font-normal">
                    <span>
                      {name(option.code)}{" "}
                      <span className="text-muted-foreground">({option.code})</span>
                    </span>
                    {counts(option.books, option.editions) ? (
                      <span className="text-muted-foreground text-xs">
                        {counts(option.books, option.editions)}
                      </span>
                    ) : null}
                  </Label>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-2">
              <Label htmlFor="language-add">{t("languages.addLabel")}</Label>
              <NativeSelect
                id="language-add"
                className="w-56 max-w-full"
                value={toAdd}
                onChange={(event) => setToAdd(event.target.value)}
              >
                <option value="">{t("languages.addPlaceholder")}</option>
                {addable.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label} ({l.code})
                  </option>
                ))}
              </NativeSelect>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!toAdd}
              onClick={() => {
                setAdded((list) => [...list, toAdd]);
                onChange({ ...value, languages: [...chosen, toAdd] });
                setToAdd("");
              }}
            >
              {t("languages.add")}
            </Button>
          </div>
          {chosen.length === 0 ? (
            <p role="alert" className="text-destructive text-sm font-medium">
              {t("languages.atLeastOne")}
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-destructive text-sm font-medium">
          {errorText(error, "invalid")}
        </p>
      ) : null}

      <div className="flex items-start gap-3">
        <input
          id="setting-include-unknown"
          type="checkbox"
          className="accent-primary mt-1 size-4"
          checked={value.includeUnknown}
          aria-describedby="setting-include-unknown-help"
          onChange={(event) => onChange({ ...value, includeUnknown: event.target.checked })}
        />
        <div className="grid gap-1">
          <Label htmlFor="setting-include-unknown">
            {tc("suggestionLanguages.includeUnknown")}
          </Label>
          <p id="setting-include-unknown-help" className="text-muted-foreground text-sm">
            {t("languages.includeUnknownHelp")}
          </p>
        </div>
      </div>

      <ul className="text-muted-foreground list-disc pl-5 text-sm">
        <li>{t("languages.notStored")}</li>
        <li>{t("languages.immediately")}</li>
        <li>
          {t("languages.newLanguages")}{" "}
          <Link to="/admin/author-mapping" className="text-foreground underline underline-offset-4">
            {t("languages.syncLink")}
          </Link>
        </li>
        <li>{t("languages.perUser")}</li>
      </ul>
    </fieldset>
  );
}
